import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useUserMemberships, useSwitchCompany } from '@/hooks/company-membership/use-company-membership';
import { useCompanyStore } from '@/store/use-company-store';
import { useAuthStore } from '@/store/use-auth-store';
import { Icon } from '@iconify/react';
import { cn } from '@/lib/utils';

const CompanySwitcher: React.FC = () => {
  const { t } = useTranslation();
  const { currentCompanyId, isSwitching } = useCompanyStore();
  const { isAuthenticated } = useAuthStore();
  const { data: memberships, isLoading } = useUserMemberships({ 
    enabled: isAuthenticated 
  });
  const switchCompanyMutation = useSwitchCompany();

  // Don't render if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  if (!memberships || memberships.length === 0) {
    return null;
  }

  if (memberships.length === 1) {
    const membership = memberships[0];
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-gray-50 border border-gray-200">
        <Icon icon="mdi:office-building" className="h-4 w-4 text-gray-600" />
        <span className="text-sm font-medium text-gray-700 truncate max-w-[200px]">
          {membership.company?.name || 'Company'}
        </span>
      </div>
    );
  }

  const currentMembership = memberships.find((m) => m.companyId === currentCompanyId) || memberships[0];
  const currentCompany = currentMembership?.company;

  const handleValueChange = (value: string) => {
    if (switchCompanyMutation.isPending || isSwitching) {
      return;
    }
    
    if (value !== currentCompanyId) {
      switchCompanyMutation.mutate({ companyId: value, suppressToast: false });
    }
  };

  return (
    <Select
      value={currentCompanyId || undefined}
      onValueChange={handleValueChange}
      disabled={isLoading || switchCompanyMutation.isPending || isSwitching}
    >
      <SelectTrigger
        className={cn(
          'w-[200px] h-9 border-gray-200 bg-white hover:bg-gray-50',
          switchCompanyMutation.isPending && 'opacity-50 cursor-wait'
        )}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Icon icon="mdi:office-building" className="h-4 w-4 text-gray-600 flex-shrink-0" />
          <SelectValue placeholder={t('company.selectCompany') || 'Select company'}>
            {currentCompany?.name || 'Company'}
          </SelectValue>
        </div>
      </SelectTrigger>
      <SelectContent>
        {memberships.map((membership) => {
          const company = membership.company;
          if (!company) return null;

          const isActive = membership.companyId === currentCompanyId;
          const memberCount = company._count?.memberships ?? 0;

          return (
            <SelectItem
              key={membership.id}
              value={membership.companyId}
              className={cn(
                'cursor-pointer',
                isActive && 'bg-gray-50 font-medium'
              )}
            >
              <div className="flex flex-col gap-1 w-full">
                <div className="flex items-center justify-between">
                  <span className="font-medium truncate">{company.name}</span>
                  {isActive && (
                    <Icon icon="mdi:check" className="h-4 w-4 text-primary flex-shrink-0 ml-2" />
                  )}
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span className="capitalize">{membership.role.toLowerCase()}</span>
                  <span>•</span>
                  <span>{memberCount} {memberCount === 1 ? 'member' : 'members'}</span>
                </div>
              </div>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
};

export default CompanySwitcher;

